import React, {
  useState, useEffect, useMemo, useCallback, useRef,
} from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowDown, ArrowUp, ArrowUpDown, Search, UserPlus, X,
} from 'lucide-react';
import { getCourse, getCourseEnrollments } from '../../api/courses';
import { getTeams, addTeamMember } from '../../api/teams';
import { unwrapList } from '../../utils/api';
import SearchableCourseSelect from '../../components/SearchableCourseSelect/SearchableCourseSelect';
import LoadingState from '../../components/LoadingState/LoadingState';
import InlineEmpty from '../../components/InlineEmpty/InlineEmpty';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import Dropdown from '../../components/Dropdown/Dropdown';
import StudentAvatar from '../../components/Student360/StudentAvatar';
import { STUDENT_TIME_ZONE } from '../../components/Student360/time';
import { formatDate, formatPhone } from '../../utils/datetime';
import { useI18n } from '../../i18n/I18nContext';
import { groupTone } from './groupTone';
import TableScroll from './TableScroll';
import './Students.css';

/* ── Helpers ───────────────────────────────────────────────────────── */

function deriveStatus(e) {
  // No access comes first: a refunded, revoked or expired enrollment is not a learner.
  if (e.isActive === false) return 'inactive';
  const p = e.progressPercent ?? e.progress ?? 0;
  if (e.completedAt || p >= 100) return 'completed';
  if (p > 0) return 'in-progress';
  return 'not-started';
}

const STATUS_LABEL_KEY = {
  completed: 'statusCompleted',
  'in-progress': 'statusInProgress',
  'not-started': 'statusNotStarted',
  inactive: 'statusInactive',
};

// GET /courses/:id/enrollments serves at most 100 rows per page. The summary, status chips, search,
// sort and group filter all work on the whole roster, so every page is read (up to this many).
const ROSTER_PAGE_SIZE = 100;
const MAX_ROSTER_PAGES = 20;

async function fetchRoster(courseId) {
  const rows = [];
  let meta = null;
  for (let page = 1; page <= MAX_ROSTER_PAGES; page += 1) {
    const res = await getCourseEnrollments(courseId, { limit: ROSTER_PAGE_SIZE, page });
    rows.push(...(res.data || []));
    meta = res.meta || meta;
    if (!res.meta || page >= res.meta.totalPages) break;
  }
  return { rows, meta };
}

function progressTone(p) {
  if (p >= 100) return 'is-done';
  if (p > 0) return 'is-some';
  return 'is-zero';
}

const FILTERS = [
  { key: 'all', labelKey: 'filterAll' },
  { key: 'in-progress', labelKey: 'filterInProgress' },
  { key: 'completed', labelKey: 'filterCompleted' },
  { key: 'not-started', labelKey: 'filterNotStarted' },
  { key: 'inactive', labelKey: 'filterInactive' },
];
const FILTER_KEYS = FILTERS.map((f) => f.key);
const SORT_KEYS = ['name', 'progress', 'status', 'enrolled'];

// The width the "add to group" menu is drawn at (CSS) and the gap it keeps from the screen edge.
const GROUP_MENU_WIDTH = 224;
const GROUP_MENU_EDGE = 8;
const SEARCH_URL_DELAY_MS = 300;
const NO_ROWS = [];
// A course id from the URL ends up in an API path: letters, digits, `_` and `-` only (a UUID, or the
// slug some seeded courses have), never a slash or a dot.
const SAFE_COURSE_ID = /^[\w-]{1,100}$/;

function SortHead({
  label, k, sort, onSort, className = '',
}) {
  const active = sort.key === k;
  let icon = <ArrowUpDown size={13} className="sr-sort-ico" aria-hidden="true" />;
  if (active) icon = sort.dir === 'asc' ? <ArrowUp size={13} aria-hidden="true" /> : <ArrowDown size={13} aria-hidden="true" />;
  let ariaSort = 'none';
  if (active) ariaSort = sort.dir === 'asc' ? 'ascending' : 'descending';
  return (
    <th className={`sr-th ${className}`} aria-sort={ariaSort}>
      <button type="button" className={`sr-sort ${active ? 'is-active' : ''}`} onClick={() => onSort(k)}>
        <span>{label}</span>
        {icon}
      </button>
    </th>
  );
}

/* ── Component ─────────────────────────────────────────────────────── */

/**
 * The By-course tab: the roster of one course, with every filter in the URL (`courseId`, `q`,
 * `status`, `group`, `sort`, `dir`), so a student page opened from a row comes back to the same
 * list, and a link to a course roster can be shared.
 */
function Students({ embedded = false }) {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const courseParam = searchParams.get('courseId') || '';
  const selectedCourseId = SAFE_COURSE_ID.test(courseParam) ? courseParam : '';
  const filterParam = searchParams.get('status');
  const filter = FILTER_KEYS.includes(filterParam) ? filterParam : 'all';
  const groupFilter = searchParams.get('group') || 'all';
  const sortParam = searchParams.get('sort');
  const sort = {
    key: SORT_KEYS.includes(sortParam) ? sortParam : 'name',
    dir: searchParams.get('dir') === 'desc' ? 'desc' : 'asc',
  };
  const urlSearch = searchParams.get('q') || '';

  // Writes some of the roster's own params and leaves every other one (`tab`) alone.
  const patch = useCallback((changes) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(changes).forEach(([key, value]) => {
        if (value === '' || value === null || value === undefined) next.delete(key);
        else next.set(key, String(value));
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // The roster of the course in the URL. `courseId` is the course the rows belong to: until it
  // matches the one asked for, the page is loading (never the rows of the course picked before).
  const [roster, setRoster] = useState({
    courseId: '', rows: [], meta: null, error: null,
  });
  const [reloadToken, setReloadToken] = useState(0);
  const [courseTitle, setCourseTitle] = useState({ id: '', title: '' });

  useEffect(() => {
    if (!selectedCourseId) return undefined;
    let cancelled = false;
    setRoster((current) => (current.courseId ? { courseId: '', rows: [], meta: null, error: null } : current));
    fetchRoster(selectedCourseId)
      .then((res) => {
        if (!cancelled) setRoster({
          courseId: selectedCourseId, rows: res.rows, meta: res.meta, error: null,
        });
      })
      .catch((err) => {
        if (!cancelled) setRoster({
          courseId: selectedCourseId, rows: [], meta: null, error: err || {},
        });
      });
    return () => { cancelled = true; };
  }, [selectedCourseId, reloadToken]);

  // The picker shows the course it knows; a course that came from the URL (and is not among the
  // first it loaded) is named here, so the picker never reads as "pick a course" over a full roster.
  useEffect(() => {
    if (!selectedCourseId) return undefined;
    let cancelled = false;
    getCourse(selectedCourseId)
      .then((course) => { if (!cancelled && course?.title) setCourseTitle({ id: selectedCourseId, title: course.title }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedCourseId]);

  const ready = Boolean(selectedCourseId) && roster.courseId === selectedCourseId;
  const loading = Boolean(selectedCourseId) && !ready;
  const { error, meta } = ready ? roster : { error: null, meta: null };
  const enrollments = ready ? roster.rows : NO_ROWS;

  // The search box types into local state (the list narrows at once) and reaches the URL after a pause.
  const [search, setSearch] = useState(urlSearch);
  const pushedSearch = useRef(urlSearch);
  useEffect(() => {
    if (urlSearch !== pushedSearch.current) {
      pushedSearch.current = urlSearch;
      setSearch(urlSearch);
    }
  }, [urlSearch]);
  useEffect(() => {
    const trimmed = search.trim();
    if (trimmed === pushedSearch.current) return undefined;
    const timer = setTimeout(() => {
      pushedSearch.current = trimmed;
      patch({ q: trimmed });
    }, SEARCH_URL_DELAY_MS);
    return () => clearTimeout(timer);
  }, [search, patch]);

  // Groups (teams) — used to show membership chips and the "add to group" menu.
  const [teams, setTeams] = useState([]);
  const [groupMenu, setGroupMenu] = useState(null); // { uid, left, top?, bottom? } | null
  const [busyUid, setBusyUid] = useState(null);

  // Load groups once (degrades gracefully if the endpoint isn't available yet).
  useEffect(() => {
    let cancelled = false;
    getTeams().then((r) => { if (!cancelled) setTeams(unwrapList(r)); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Close the group menu on outside click / scroll / escape.
  useEffect(() => {
    if (!groupMenu) return undefined;
    const close = () => setGroupMenu(null);
    const onKey = (e) => { if (e.key === 'Escape') setGroupMenu(null); };
    const onDoc = (e) => { if (!e.target.closest?.('.sr-group-menu, .sr-group-add')) setGroupMenu(null); };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDoc);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDoc);
    };
  }, [groupMenu]);

  // Decorate rows once with derived status + normalized progress.
  const rows = useMemo(
    () => enrollments.map((e) => ({
      ...e,
      _status: deriveStatus(e),
      _progress: Math.round(e.progressPercent ?? e.progress ?? 0),
      _name: e.user?.name || '—',
      _email: e.user?.email || '',
      _phone: e.user?.phone || '',
      _uid: e.userId || e.user?.id || null,
    })),
    [enrollments],
  );

  // Map userId -> [{ id, name, type }] from the loaded groups.
  const groupsByUser = useMemo(() => {
    const map = {};
    teams.forEach((tm) => (tm.members || []).forEach((m) => {
      if (!m.userId) return;
      (map[m.userId] || (map[m.userId] = [])).push({ id: tm.id, name: tm.name, type: tm.type });
    }));
    return map;
  }, [teams]);

  // Learners only: an enrollment without access is neither a student to count nor progress to average.
  const stats = useMemo(() => {
    const live = rows.filter((r) => r._status !== 'inactive');
    const total = live.length;
    const avg = total ? Math.round(live.reduce((s, r) => s + r._progress, 0) / total) : 0;
    return { total, avg };
  }, [rows]);

  const groupOptions = useMemo(() => [
    { value: 'all', label: t('student360.roster.allGroups') },
    ...teams.map((tm) => ({ value: tm.id, label: tm.name })),
  ], [teams, t]);

  const filtered = useMemo(() => {
    let list = rows;
    if (filter !== 'all') list = list.filter((r) => r._status === filter);
    if (groupFilter !== 'all') list = list.filter((r) => (groupsByUser[r._uid] || []).some((g) => g.id === groupFilter));
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      // Phones are stored as +998901234567: compare digits, so "90 123" finds it.
      const qDigits = q.replace(/\D/g, '');
      list = list.filter((r) => r._name.toLowerCase().includes(q)
        || r._email.toLowerCase().includes(q)
        || (qDigits.length >= 3 && r._phone.replace(/\D/g, '').includes(qDigits)));
    }
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      let av; let bv;
      switch (sort.key) {
        case 'progress': av = a._progress; bv = b._progress; break;
        case 'enrolled': av = a.enrolledAt || ''; bv = b.enrolledAt || ''; break;
        case 'status': av = a._status; bv = b._status; break;
        default: av = a._name.toLowerCase(); bv = b._name.toLowerCase();
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [rows, filter, groupFilter, groupsByUser, search, sort.key, sort.dir]);

  const toggleSort = useCallback((key) => {
    if (sort.key === key) patch({ sort: key, dir: sort.dir === 'asc' ? 'desc' : 'asc' });
    else patch({ sort: key, dir: 'asc' });
  }, [patch, sort.key, sort.dir]);

  const pickCourse = (courseId) => {
    // A new course starts with a clean list: the filters of the one before are not its filters.
    pushedSearch.current = '';
    setSearch('');
    setCourseTitle({ id: '', title: '' });
    patch({
      courseId, q: '', status: '', group: '', sort: '', dir: '',
    });
  };

  // The menu opens under its button, or above it near the bottom edge, and never past the screen sides.
  const openGroupMenu = (uid, ev) => {
    if (groupMenu?.uid === uid) { setGroupMenu(null); return; }
    const rect = ev.currentTarget.getBoundingClientRect();
    const left = Math.max(
      GROUP_MENU_EDGE,
      Math.min(rect.right - GROUP_MENU_WIDTH, window.innerWidth - GROUP_MENU_WIDTH - GROUP_MENU_EDGE),
    );
    const roomBelow = window.innerHeight - rect.bottom;
    setGroupMenu(roomBelow < 240 && rect.top > roomBelow
      ? { uid, left, bottom: window.innerHeight - rect.top + 4 }
      : { uid, left, top: rect.bottom + 4 });
  };

  const addToGroup = useCallback(async (uid, team) => {
    setBusyUid(uid);
    setGroupMenu(null);
    try {
      const updated = await addTeamMember(team.id, { userId: uid });
      setTeams((prev) => prev.map((tm) => (tm.id === updated.id ? updated : tm)));
      toast.success(t('student360.roster.addedToGroup', { group: team.name }));
    } catch {
      toast.error(t('student360.roster.addGroupFailed'));
    } finally {
      setBusyUid(null);
    }
  }, [t]);

  const detailPath = (row) => `/students/${row._uid}?courseId=${selectedCourseId}`;

  const openDetail = useCallback((row) => {
    if (!row._uid) return;
    navigate(`/students/${row._uid}?courseId=${selectedCourseId}`);
  }, [navigate, selectedCourseId]);

  // The whole row opens the student; the links and buttons inside keep their own meaning.
  const handleRowClick = (event, row) => {
    if (event.target.closest('a, button')) return;
    openDetail(row);
  };

  // Groups the menu's current student is NOT already in.
  const menuOptions = groupMenu
    ? teams.filter((tm) => !(groupsByUser[groupMenu.uid] || []).some((g) => g.id === tm.id))
    : [];

  const forbidden = error?.code === 403 || error?.response?.status === 403;

  let content;
  if (!selectedCourseId) {
    content = (
      <PageEmptyState
        variant="students"
        title={t('student360.roster.pickTitle')}
        description={t('student360.roster.pickDesc')}
      />
    );
  } else if (loading) {
    content = <LoadingState skeleton="table" rows={6} message={t('student360.roster.loading')} />;
  } else if (error) {
    content = (
      <PageEmptyState
        variant={forbidden ? 'forbidden' : 'error'}
        title={forbidden ? t('student360.detail.noAccessTitle') : t('student360.errors.title')}
        description={forbidden ? t('student360.errors.noAccessDesc') : t('student360.errors.desc')}
        actionLabel={forbidden ? undefined : t('student360.errors.retry')}
        onAction={forbidden ? undefined : () => setReloadToken((n) => n + 1)}
      />
    );
  } else if (rows.length === 0) {
    content = (
      <PageEmptyState
        variant="students"
        title={t('student360.roster.emptyTitle')}
        description={t('student360.roster.emptyDesc')}
      />
    );
  } else {
    content = (
      <>
        {/* Filters: status chips (they carry the counts) + group filter */}
        <div className="sr-filters">
          <div className="ui-chip-row ui-chip-row--wrap sr-filter-chips" role="group" aria-label={t('student360.roster.colStatus')}>
            {FILTERS.map((f) => {
              const count = f.key === 'all' ? rows.length : rows.filter((r) => r._status === f.key).length;
              // Only courses with refunded or expired enrollments need the "No access" chip.
              if (f.key === 'inactive' && count === 0 && filter !== f.key) return null;
              const on = filter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  className={`ui-chip ${on ? 'is-active' : ''}`}
                  aria-pressed={on}
                  onClick={() => patch({ status: f.key === 'all' ? '' : f.key })}
                >
                  {t(`student360.roster.${f.labelKey}`)}
                  <span className="ui-count">{count}</span>
                </button>
              );
            })}
          </div>
          {teams.length > 0 && (
            <div className="sr-filter-group">
              <span className="sr-filter-label">{t('student360.roster.colGroups')}</span>
              <Dropdown
                value={groupFilter}
                onChange={(value) => patch({ group: value === 'all' ? '' : value })}
                options={groupOptions}
                className="sr-group-filter"
                ariaLabel={t('student360.roster.colGroups')}
                size="sm"
              />
            </div>
          )}
        </div>

        {/* Roster table */}
        <div className="ui-table-card sr-table-card">
          {filtered.length === 0 ? (
            <InlineEmpty kind="search" title={t('student360.roster.noMatch')} className="sr-inline-empty" />
          ) : (
            <TableScroll>
              <table className="ui-table sr-table">
                <thead>
                  <tr>
                    <SortHead label={t('student360.roster.colStudent')} k="name" sort={sort} onSort={toggleSort} className="col-student" />
                    <SortHead label={t('student360.roster.colProgress')} k="progress" sort={sort} onSort={toggleSort} className="col-progress" />
                    <SortHead label={t('student360.roster.colStatus')} k="status" sort={sort} onSort={toggleSort} className="col-status" />
                    <th className="sr-th col-groups">{t('student360.roster.colGroups')}</th>
                    <SortHead label={t('student360.roster.colEnrolled')} k="enrolled" sort={sort} onSort={toggleSort} className="col-date" />
                    <th className="sr-th col-date">{t('student360.roster.colCompleted')}</th>
                    <th className="sr-th col-actions" aria-label={t('student360.roster.addToGroup')} />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => {
                    const contact = [r._email, r._phone ? formatPhone(r._phone) : ''].filter(Boolean).join(' · ');
                    const person = (
                      <>
                        <StudentAvatar name={r._name} avatar={r.user?.avatar} seed={r._email || r._uid || r.id} />
                        <span className="ui-cell-person__text">
                          <span className="ui-cell-person__name">{r._name}</span>
                          {contact && <span className="ui-cell-person__sub" title={contact}>{contact}</span>}
                        </span>
                      </>
                    );
                    const groups = groupsByUser[r._uid] || [];
                    return (
                      <tr
                        key={r.id}
                        className={`${r._status === 'inactive' ? 'is-inactive' : ''} ${r._uid ? 'is-clickable' : ''}`.trim()}
                        onClick={(event) => handleRowClick(event, r)}
                      >
                        <td className="col-student">
                          {r._uid ? (
                            <Link
                              className="ui-cell-person sr-student"
                              to={detailPath(r)}
                              title={t('student360.roster.viewDetail')}
                            >
                              {person}
                            </Link>
                          ) : (
                            <span className="ui-cell-person sr-student">{person}</span>
                          )}
                        </td>
                        <td className="col-progress" data-label={t('student360.roster.colProgress')}>
                          <div className="sr-progress">
                            <div className="sr-progress-track">
                              <div
                                className={`sr-progress-fill ${progressTone(r._progress)}`}
                                style={{ width: `${Math.min(100, Math.max(0, r._progress))}%` }}
                              />
                            </div>
                            <span className="sr-progress-val">{r._progress}%</span>
                          </div>
                        </td>
                        <td className="col-status" data-label={t('student360.roster.colStatus')}>
                          <StatusBadge status={r._status}>{t(`student360.roster.${STATUS_LABEL_KEY[r._status]}`)}</StatusBadge>
                        </td>
                        <td className="col-groups" data-label={t('student360.roster.colGroups')}>
                          <div className="sr-groups">
                            {groups.length === 0 && <span className="s360-muted">—</span>}
                            {groups.map((g) => (
                              <span key={g.id} className="s360-team" data-tone={groupTone(g.type)} title={g.name}>
                                {g.name}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="col-date" data-label={t('student360.roster.colEnrolled')}>
                          {r.enrolledAt ? formatDate(r.enrolledAt, { lang, timeZone: STUDENT_TIME_ZONE }) : '—'}
                        </td>
                        <td className="col-date" data-label={t('student360.roster.colCompleted')}>
                          {r.completedAt ? formatDate(r.completedAt, { lang, timeZone: STUDENT_TIME_ZONE }) : '—'}
                        </td>
                        <td className="col-actions">
                          {r._uid && (
                            <button
                              type="button"
                              className="ui-icon-btn sr-group-add"
                              disabled={busyUid === r._uid}
                              aria-haspopup="menu"
                              aria-expanded={groupMenu?.uid === r._uid}
                              aria-label={`${t('student360.roster.addToGroup')}: ${r._name}`}
                              title={t('student360.roster.addToGroup')}
                              onClick={(ev) => openGroupMenu(r._uid, ev)}
                            >
                              <UserPlus aria-hidden="true" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TableScroll>
          )}
          <div className="ui-table-footer sr-footer">
            {meta?.total
              ? t('student360.roster.showing', { shown: filtered.length, total: meta.total })
              : t('student360.roster.showingAll', { shown: filtered.length })}
            {meta?.total > enrollments.length && (
              <span className="sr-footer-note">
                {t('student360.roster.rosterCapped', { loaded: enrollments.length, total: meta.total })}
              </span>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <div className={`students-redesign ${embedded ? 'is-embedded' : ''}`}>
      {!embedded && (
        <div className="page-head">
          <div>
            <h1 className="page-title">{t('student360.hub.title')}</h1>
            <p className="page-sub">{t('student360.roster.subtitle')}</p>
          </div>
        </div>
      )}

      {/* Toolbar: the course, the search, and what the roster adds up to */}
      <div className="sr-toolbar">
        <div className="sr-controls">
          <div className="sr-course">
            <SearchableCourseSelect
              value={selectedCourseId}
              onChange={pickCourse}
              placeholder={(selectedCourseId && courseTitle.id === selectedCourseId && courseTitle.title)
                || t('student360.roster.selectCourse')}
              size="sm"
            />
          </div>
          <label className="ds-search sr-search">
            <Search size={16} aria-hidden="true" />
            <input
              type="search"
              placeholder={selectedCourseId ? t('student360.roster.searchPlaceholder') : t('student360.roster.searchDisabled')}
              aria-label={t('student360.roster.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={!selectedCourseId}
              autoComplete="off"
            />
            {search && (
              <button type="button" className="ds-search-clear" onClick={() => setSearch('')} aria-label={t('common.clear')}>
                <X aria-hidden="true" />
              </button>
            )}
          </label>
        </div>
        {ready && !error && rows.length > 0 && (
          <p className="sr-roster-sub">
            {t('student360.roster.rosterCount', { count: stats.total })}
            {' · '}
            {t('student360.roster.avgLine', { percent: stats.avg })}
          </p>
        )}
      </div>

      {content}

      {/* Fixed-position "add to group" menu (escapes the table's overflow clip) */}
      {groupMenu && (
        <div
          className="sr-group-menu"
          role="menu"
          style={{
            left: groupMenu.left, top: groupMenu.top, bottom: groupMenu.bottom,
          }}
        >
          <div className="sr-group-menu-head">{t('student360.roster.assignHead')}</div>
          {teams.length === 0 ? (
            <div className="sr-group-menu-empty">{t('student360.roster.noGroupsYet')}</div>
          ) : menuOptions.length === 0 ? (
            <div className="sr-group-menu-empty">{t('student360.roster.inAllGroups')}</div>
          ) : (
            menuOptions.map((tm) => (
              <button
                key={tm.id}
                type="button"
                role="menuitem"
                className="sr-group-menu-item"
                data-tone={groupTone(tm.type)}
                onClick={() => addToGroup(groupMenu.uid, tm)}
              >
                <span className="sr-group-dot" />
                {tm.name}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default Students;
